// Genera l'HTML della firma email a partire dalla riga DB `email_signatures`.
// Replica fedelmente la logica di EmailSignatureGenerator.generateSignatureHTML
// per essere identico a quanto mostrato/scaricato nella sezione Firme.

interface SignatureOptions {
  includeName?: boolean;
  includeSurname?: boolean;
  includeEmail?: boolean;
  includePhone?: boolean;
  includeRole?: boolean;
  includeWebsiteURL?: boolean;
  includeAddressFirst?: boolean;
  includeAddressSecond?: boolean;
  includeImageURL?: boolean;
  includeWhatsappURL?: boolean;
  includeCompanyDescription?: boolean;
  includeMeetingUrl?: boolean;
  includePrivacyText?: boolean;
  includeWebsiteLinkText?: boolean;
}

const DEFAULT_OPTIONS: SignatureOptions = {
  includeName: true, includeSurname: true, includeEmail: true, includePhone: true,
  includeRole: true, includeWebsiteURL: true, includeAddressFirst: false, includeAddressSecond: false,
  includeImageURL: true, includeWhatsappURL: false, includeCompanyDescription: false,
  includeMeetingUrl: false, includePrivacyText: true, includeWebsiteLinkText: true,
};

const FONT_FAMILY: Record<string, string> = {
  'Helvetica Neue, sans-serif': '"Inter", "Helvetica Neue", sans-serif',
  'Roboto, sans-serif': '"Roboto", sans-serif',
  'Open Sans, sans-serif': '"Open Sans", sans-serif',
  'Lato, sans-serif': '"Lato", sans-serif',
  'Montserrat, sans-serif': '"Montserrat", sans-serif',
  'Poppins, sans-serif': '"Poppins", sans-serif',
  'Arial, sans-serif': '"Arial", sans-serif',
  'Georgia, serif': '"Georgia", serif',
};

export function buildSignatureHtml(row: any): string {
  // Firma custom: l'HTML è fornito dall'utente (già sanitizzato al salvataggio)
  if (row.signature_type === 'custom') {
    return row.custom_html || '';
  }
  const f = {
    name: row.signature_name || '',
    surname: row.signature_surname || '',
    email: row.signature_email || '',
    phone: row.signature_phone || '',
    role: row.signature_role || '',
    websiteURL: row.signature_website_url || '',
    addressFirst: row.signature_address_first || '',
    addressSecond: row.signature_address_second || '',
    imageURL: row.signature_image_url || '',
    whatsappURL: row.signature_whatsapp_url || '',
    companyDescription: row.signature_company_description || '',
    meetingUrl: row.signature_meeting_url || '',
    meetingLinkText: row.signature_meeting_link_text || 'Schedule a meeting with me',
    websiteLinkText: row.signature_website_link_text || 'Visit our website',
    logoWidth: row.signature_logo_width || '180',
    logoAlignment: row.signature_logo_alignment || 'left',
    nameSize: row.signature_name_size || '25',
    lineSpacing: row.signature_line_spacing || '1.2',
    nameBold: row.signature_name_bold !== undefined ? row.signature_name_bold : true,
    privacyText: row.signature_privacy_text || '',
  };
  const color = row.signature_color || '#2b2b2b';
  const fontFamily = FONT_FAMILY[row.signature_font] || FONT_FAMILY['Helvetica Neue, sans-serif'];
  const o: SignatureOptions = { ...DEFAULT_OPTIONS, ...(row.signature_options || {}) };

  const phoneOrWa = (o.includePhone && f.phone) || (o.includeWhatsappURL && f.whatsappURL);
  const emailOrAddr = (o.includeEmail && f.email) || (o.includeAddressFirst && f.addressFirst) || (o.includeAddressSecond && f.addressSecond);

  return `
<div data-signature-content style="font-family:${fontFamily};color:rgb(60,60,60);">
  <table style="font-family:${fontFamily};">
    <tr>
      <td rowspan="1" style="padding:24px;text-align:${f.logoAlignment};">
        ${o.includeImageURL && f.imageURL ? `<a style="text-decoration:none;" href="${f.websiteURL}"><img alt="Logo" src="${f.imageURL}" width="${f.logoWidth}" style="display:inline-block;" /></a><br />` : ''}
        ${o.includeWebsiteURL && o.includeWebsiteLinkText && f.websiteURL && f.websiteLinkText ? `<span style="font-size:12px;color:${color};"><a style="text-decoration:none;color:${color};" href="${f.websiteURL}"><b>${f.websiteLinkText} 👉 <u>${f.websiteURL.replace(/^https?:\/\//, '')}</u></b></a></span>` : ''}
      </td>
      <td style="font-size:12px;padding-left:20px;">
        <br /><br /><br /><br />
        ${o.includeName || o.includeSurname ? `<span style="font-size:${f.nameSize}px;color:${color};line-height:${f.lineSpacing};">${o.includeName && f.name ? `<span style="font-weight:${f.nameBold ? '900' : '400'};">${String(f.name).toUpperCase()}</span>` : ''} ${o.includeSurname && f.surname ? `<span style="font-weight:400;">${String(f.surname).toUpperCase()}</span>` : ''}</span><br />` : ''}
        ${o.includeRole && f.role ? `<span style="font-size:12px;color:${color};">${f.role}</span><br /><br />` : ''}
        ${o.includeCompanyDescription && f.companyDescription ? `<div style="font-size:11px;color:${color};max-width:350px;">👨‍💻 ${f.companyDescription}</div><br />` : ''}
        ${o.includePhone && f.phone ? `<span style="font-size:11px;"><a style="text-decoration:none;color:#353a40;" href="tel:${f.phone}">📲 (+39) ${f.phone}</a>${o.includeWhatsappURL && f.whatsappURL ? ` • <a style="text-decoration:none;font-weight:bold;color:green;" href="https://api.whatsapp.com/send?phone=${f.whatsappURL}&text=">💬 WhatsApp</a>` : ''}</span>` : ''}
        ${phoneOrWa ? '<br />' : ''}
        ${o.includeEmail && f.email ? `<span style="font-size:11px;color:${color};"><a style="text-decoration:none;color:${color};" href="mailto:${f.email}">📨 ${f.email}</a></span><br /><br />` : ''}
        ${o.includeAddressFirst && f.addressFirst ? `<span style="font-size:11px;color:${color};">🏢 ${f.addressFirst}</span><br />` : ''}
        ${o.includeAddressSecond && f.addressSecond ? `<span style="font-size:11px;color:${color};">📍 ${f.addressSecond}</span><br />` : ''}
        ${emailOrAddr ? '<br />' : ''}
        ${o.includeMeetingUrl && f.meetingUrl && f.meetingLinkText ? `<span style="font-size:11px;color:${color};"><a style="text-decoration:none;color:${color};" href="${f.meetingUrl}">📅 ${f.meetingLinkText}</a></span>` : ''}
      </td>
    </tr>
  </table>
  ${o.includePrivacyText && f.privacyText ? `<p style="max-width:500px;font-size:5px;color:rgba(100,100,100,0.5);">${f.privacyText}</p>` : ''}
</div>`.trim();
}
